import { useCallback } from "react";
import { selectorFamily, useRecoilValue, useRecoilRefresher_UNSTABLE } from "recoil";
import { COMMUNICATE_ERROR_MESSAGE_THRESHOLD_COLOR_LIST, COMMUNICATE_ERROR_MESSAGE_THRESHOLD_LIST, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../constants/ErrorMessage";
import axios from "axios";
import { userState } from "./UserState";

// API
// Threshold Color
const _listThresholdColorList = async (data) => {
	const apiUrl = process.env.REACT_APP_THRESHOLD_COLOR_API_URL;
	const apiKey = process.env.REACT_APP_THRESHOLD_COLOR_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/common/threshold-color/list`,
		headers: {
			'x-api-key': apiKey
		},
		params: data,
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_THRESHOLD_COLOR_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data || [];
			return list;
		})
		.catch(() => {
			return [];
		});
}

const _updateThresholdColorList = async (data) => {
	const apiUrl = process.env.REACT_APP_THRESHOLD_COLOR_API_URL;
	const apiKey = process.env.REACT_APP_THRESHOLD_COLOR_API_KEY;
	const config = {
		method: 'PUT',
		url: `${apiUrl}/common/threshold-color/update`,
		headers: {
			'Content-Type': 'application/json',
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
}

// Threshold
const _listThresholdValue = async (data) => {
	const apiUrl = process.env.REACT_APP_THRESHOLD_API_URL;
	const apiKey = process.env.REACT_APP_THRESHOLD_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/company-setting/threshold-setting/threshold/list`,
		headers: {
			'x-api-key': apiKey
		},
		params: data,
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_THRESHOLD_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data || [];
			return list;
		})
		.catch(() => {
			return [];
		});
};

const _updateThresholdValue = async (data) => {
	const apiUrl = process.env.REACT_APP_THRESHOLD_API_URL;
	const apiKey = process.env.REACT_APP_THRESHOLD_API_KEY;
	const config = {
		method: 'PUT',
		url: `${apiUrl}/company-setting/threshold-setting/threshold/update`,
		headers: {
			'Content-Type': 'application/json',
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};


/* Threshold Color state */
const thresholdColorListState = selectorFamily({
	key: 'thresholdColorListState',
	get: (data) => () => _listThresholdColorList(data),
});

/* Threshold Value state */
const thresholdValueListState = selectorFamily({
	key: 'thresholdValueListState',
	get: (data) => () => _listThresholdValue(data),
});

export const useThresholdList = () => {
	const user = useRecoilValue(userState);
	const customerParams = { customerCode : user.customer };
	const thresholdColorList = useRecoilValue(thresholdColorListState(customerParams));
	const thresholdList = useRecoilValue(thresholdValueListState(customerParams));

	const ref1 = useRecoilRefresher_UNSTABLE(thresholdColorListState(customerParams));
	const ref2 = useRecoilRefresher_UNSTABLE(thresholdValueListState(customerParams));

	const refresher = useCallback(() => {
		ref1();
		ref2();
	}, [ref1, ref2]);

	const updateThresholdColorList = useCallback(data => {
		const require = [
			'customerCode',
			'apiExecUser',
			'thresholdColor',
		];

		const require2 = [
			'alertId',
			'alertColor',
			'updatedTime',
		];

		const _data = Object.assign({}, data, {
			customerCode: user.customer,
			apiExecUser: user.userId,
		});

		if (!require.every(v => v in _data)) {
			throw new Error('Unset require parameter');
		};

		if (!_data.thresholdColor.every(d => require2.every(v => v in d))) {
			throw new Error('Unset require parameter');
		};

		return _updateThresholdColorList(_data)
			.then(refresher)
	}, [refresher, user]);

	const updateThresholdList = useCallback(data => {
		const require = [
			'customerCode',
			'apiExecUser',
			'threshold',
		];

		const require2 = [
			'alertId',
			'active',
			'wind',
			'windActive',
			'gustWind',
			'gustWindActive',
			'vis',
			'visActive',
			'cig',
			'cigActive',
			'cloudCoverType',
			'cloudBaseAltitude',
			'cloudActive',
			'temp1',
			'temp1Active',
			'temp2',
			'temp2Active',
			'qualifierFlag',
			'lgtM',
			'mod',
			'hvyP',
			'vc',
			'mi',
			'bc',
			'pr',
			'dr',
			'bl',
			'sh',
			'ts',
			'fz',
			'obscurationFlag',
			'br',
			'fg',
			'fu',
			'va',
			'du',
			'sa',
			'hz',
			'precipitationFlag',
			'dz',
			'ra',
			'sn',
			'sg',
			'pl',
			'gr',
			'gs',
			'ic',
			'up',
			'otherFlag',
			'po',
			'sq',
			'fc',
			'ss',
			'ds',
			'py',
			'supplyFlag',
			'ws',
			're',
		];

		const _data = Object.assign({}, data, {
			customerCode: user.customer,
			apiExecUser: user.userId,
		});

		if (!require.every(v => v in _data)) {
			throw new Error('Unset require parameter');
		};

		if (!_data.threshold.every(d => require2.every(v => v in d))) {
			console.error(_data);
			throw new Error('Unset require parameter');
		};

		return _updateThresholdValue(_data)
			.then(refresher)
	}, [refresher, user]);

	return {
		thresholdColorList,
		thresholdList,
		updateThresholdColorList,
		updateThresholdList,
		refreshThresholdList: refresher,
	}
};
